import { io } from "socket.io-client";
import axios from 'axios';
import { toast } from 'react-toastify';
import baseURL from '../utils/baseURL';

let socket = null;
// Guards a re-auth attempt so a genuinely forbidden user cannot loop on it.
let reauthInFlight = false;
// Set by callers that want to retry their last request once the socket regains
// its identity — otherwise a successful re-auth would fix the socket and leave
// the screen still empty.
let onReauthenticated = null;
// The queue ('patient' | 'tenant') the agent side last asked for, so a
// reconnect can re-request the same lists without the page noticing.
let lastQueue = null;

/**
 * Give the LIVE socket a fresh identity.
 *
 * The socket's identity comes from the token presented at handshake, and access
 * tokens last about fifteen minutes. A tab left open therefore ends up with a
 * socket that is connected but anonymous, and every role-guarded event answers
 * Forbidden while the connection itself looks perfectly healthy.
 *
 * Refreshing and re-authenticating in place is deliberate: reconnecting would
 * work, but it would also drop chat and support rooms that have nothing to do
 * with the expired token.
 *
 * Uses a bare axios call rather than the app's authed instance on purpose — that
 * instance refreshes on 401, and calling it from inside a refresh is how you get
 * two refreshes racing each other.
 */
async function refreshAndReauthenticate() {
    try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken || !socket) return false;

        const res = await axios.post(`${baseURL}/auth/refresh`, { refreshToken });
        const { accessToken, refreshToken: newRefresh } = res.data?.data || {};
        if (!accessToken) return false;

        localStorage.setItem('accessToken', accessToken);
        if (newRefresh) localStorage.setItem('refreshToken', newRefresh);

        // Keep the handshake copies current too, so a later reconnect carries
        // the fresh token rather than the one that just expired.
        socket.auth = { ...(socket.auth || {}), token: accessToken };
        // Never in the URL — see "NO SECRETS IN THE URL" below.
        if (socket.io?.opts?.query) {
            const { token: _dropToken, fcmToken: _dropFcm, ...rest } = socket.io.opts.query;
            socket.io.opts.query = rest;
        }

        return await new Promise((resolve) => {
            let settled = false;
            const done = (v) => { if (!settled) { settled = true; resolve(v); } };
            // If the server is older than this event, the ack never comes —
            // resolve false rather than hanging the caller forever.
            setTimeout(() => done(false), 5000);
            socket.emit('authenticate', { token: accessToken }, (r) => done(Boolean(r?.ok)));
        });
    } catch (err) {
        console.error('🔑 Support socket re-authentication failed:', err?.message);
        return false;
    }
}

/** Register what to re-run once the socket regains its identity. */
export const setOnReauthenticated = (fn) => { onReauthenticated = typeof fn === 'function' ? fn : null; };

// The server's "you are nobody / not allowed" answers. UNAUTHENTICATED and
// SUPPORT_UNAUTHENTICATED mean the handshake token no longer identifies us
// (expired while the tab sat open); FORBIDDEN can mean the same thing seen
// from a role check, or a genuine lack of permission — re-auth tells the two
// apart: it fixes the first and changes nothing about the second.
const REAUTH_CODES = new Set(['UNAUTHENTICATED', 'SUPPORT_UNAUTHENTICATED', 'FORBIDDEN']);
// After a SUCCESSFUL re-auth the same code arriving again within this window
// is a real refusal, not a stale identity — hand it to the page instead of
// refreshing again, so a genuinely forbidden user cannot loop:
// refresh → retry → FORBIDDEN → refresh → …
const REAUTH_COOLDOWN_MS = 30 * 1000;
let reauthCooldownUntil = 0;

/**
 * Try to recover from an auth-shaped error IN PLACE (same connection, so the
 * chat and support rooms survive). Returns true when it took the error over:
 * either a re-auth is running (a burst of refusals triggers ONE refresh) or
 * one was just started. On success the registered onReauthenticated hook
 * re-runs the caller's last request; on failure `callback` gets the original
 * error, exactly as if nothing had been attempted.
 *
 * Returns false when the error is not auth-shaped, or a re-auth just
 * succeeded and the code is back anyway — the caller handles it as usual.
 */
const handleAuthError = (err, callback) => {
    if (!REAUTH_CODES.has(err?.code)) return false;
    if (Date.now() < reauthCooldownUntil) return false;
    if (reauthInFlight) return true;
    reauthInFlight = true;
    refreshAndReauthenticate()
        .then((ok) => {
            if (ok) {
                console.log('🔑 Support socket re-authenticated');
                reauthCooldownUntil = Date.now() + REAUTH_COOLDOWN_MS;
                if (typeof onReauthenticated === 'function') onReauthenticated();
            } else if (typeof callback === 'function') {
                callback(err);
            }
        })
        .finally(() => { reauthInFlight = false; });
    return true;
};
let isConnecting = false;
let connectionRole = null; // Track which role connected
let connectionId = null; // Track which user ID connected
let socketWarningShown = false; // Track if we've already warned about missing socket to avoid spam
// Whether this socket instance has connected before — a second `connect` is a
// reconnect, and the server-side rooms did not survive it.
let hasConnectedOnce = false;

// The two identities that work a Super Admin support queue. 'admin' is how the
// super admin is stored client-side (authSlice normalises the role); platform
// staff keep their own role because their permissions, not a flag, decide what
// they may open.
const AGENT_ROLES = ['admin', 'platform_staff'];
const isAgentRole = (role) => AGENT_ROLES.includes(role);

// Store callbacks for listeners that need to be attached when socket connects
const pendingCallbacks = {
    admin: {
        receiveMessage: null
    },
    user: {
        receiveMessage: null
    }
};

// Get socket URL from environment - use VITE_WS_BASE_URL or fallback to API base URL
const getSocketUrl = () => {
    // Try VITE_WS_BASE_URL first (for WebSocket-specific URL)
    let wsBaseUrl = import.meta.env.VITE_WS_BASE_URL;

    // Fallback to VITE_API_BASE_URL if WS_BASE_URL is not set
    if (!wsBaseUrl) {
        wsBaseUrl = import.meta.env.VITE_API_BASE_URL;
    }

    // Final fallback to default development URL
    if (!wsBaseUrl) {
        wsBaseUrl = 'http://feelbetter-dev.it-mitra.com/api';
        console.warn('⚠️ Socket: Using default WebSocket URL. Set VITE_WS_BASE_URL or VITE_API_BASE_URL in .env file.');
    }

    // Remove /api suffix if present and trim any whitespace
    const socketUrl = wsBaseUrl.trim().replace(/\/api\/?$/, '');

    console.log('🔌 Socket URL:', socketUrl);

    return socketUrl;
};

/**
 * Connect socket with role-based authentication
 * @param {Object} params - Connection parameters
 * @param {string} params.role - User role (admin, platform_staff, doctor, patient)
 * @param {string} params.id - User ID
 * @returns {Object} Socket instance
 */
export const connectSupportSocket = ({ role, id }) => {
    // Check if user is authenticated before creating socket
    const userId = localStorage.getItem('ID');
    const userRole = localStorage.getItem('Role');
    const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';

    // Don't create socket if user is not authenticated
    if (!userId || !userRole || !isAuthenticated) {
        console.log('📵 Support socket: User not authenticated, skipping connection');
        return null;
    }

    // Validate that the provided role and id match the authenticated user
    if (role !== userRole || id !== userId) {
        console.warn('⚠️ Support socket: Role/ID mismatch with authenticated user');
        return null;
    }

    // If socket exists and is connected with the same role/id, return it
    if (socket?.connected && connectionRole === role && connectionId === id) {
        console.log('✅ Support socket already connected:', socket.id);
        return socket;
    }

    // If trying to connect with different user, disconnect first
    if (socket && (connectionRole !== role || connectionId !== id)) {
        console.log('🔄 Different user detected, disconnecting old socket');
        socket.disconnect();
        socket = null;
    }

    // If connection is in progress, wait
    if (isConnecting) {
        console.log('⏳ Support socket connection in progress');
        return socket;
    }

    // If socket exists but is disconnected, reconnect it
    if (socket && !socket.connected) {
        console.log('🔄 Support socket exists but disconnected, reconnecting...');
        socket.connect();
        connectionRole = role;
        connectionId = id;
        return socket;
    }

    // Create new socket connection
    console.log('🆕 Creating NEW socket connection for:', { role, id });
    isConnecting = true;
    connectionRole = role;
    connectionId = id;
    socketWarningShown = false; // Reset warning flag when creating new socket
    hasConnectedOnce = false;

    try {
        const socketUrl = getSocketUrl();

        if (!socketUrl) {
            console.error('❌ Cannot create socket: No valid socket URL available');
            isConnecting = false;
            toast.warning('Support service configuration error. Please contact support.');
            return null;
        }

        console.log('🔌 Connecting support socket to:', socketUrl);

        // THE TOKEN is the identity. Without it socketAuthMiddleware leaves
        // `socket.user` unset and every guarded event answers Forbidden.
        //
        // It is also the only honest way to prove who is connecting. The ids
        // in the query below are presence-only on the server once a verified
        // user exists — anyone can type one — so nothing that decides access
        // may ever read them. The signed token is what the server verifies.
        const accessToken = localStorage.getItem('accessToken');

        // Legacy ids in query params. Platform staff send none: their identity
        // is the token alone.
        const queryParams = {};
        if (role === 'admin') {
            queryParams.adminId = id;
        } else if (role === 'doctor') {
            queryParams.doctorId = id;
        } else if (role === 'patient') {
            queryParams.patientId = id;
        }

        // NO SECRETS IN THE URL (2 Oct 2026). The access token travels ONLY in
        // `auth` (Socket.IO's CONNECT packet, never part of the address) — the
        // server reads auth.token first (socket/socketAuth.js). It used to be
        // copied into the query too, so every proxy access log held a live
        // token. The FCM device token is not read from the socket at all (push
        // registration goes through the REST API), so it is not sent either.

        // Socket.IO configuration
        // Uses default /socket.io/ path (matches Apache proxy: /socket.io/ → backend:5000/socket.io/)
        // Apache handles WebSocket upgrade via ProxyPass with upgrade=WebSocket
        console.log('🔨 Creating new Socket.IO instance with config:', {
            url: socketUrl,
            queryParams,
            transports: ["websocket", "polling"]
        });

        socket = io(socketUrl, {
            // Sent BOTH ways on purpose: `auth` is the modern channel, `query`
            // is what survives a polling fallback through some proxies. The
            // server reads either.
            auth: accessToken ? { token: accessToken } : undefined,
            // No path option = use default /socket.io/ (matches Apache config)
            transports: ["websocket", "polling"],  // WebSocket first, fallback to polling
            upgrade: true,
            autoConnect: true,
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            timeout: 20000,
            query: queryParams  // Backend reads from socket.handshake.query
        });

        console.log('✅ Socket.IO instance created:', socket);
        console.log('🔌 Socket connecting state:', socket.connecting);
        console.log('🔌 Socket connected state:', socket.connected);

        // A reconnect re-runs the handshake, and the handshake carries the
        // token captured when the socket was CREATED. Fifteen minutes later
        // that token is expired and the server would accept the connection
        // as nobody. Present whatever token the app holds now instead. This
        // is a Manager event (socket.io), not a Socket one — on the socket it
        // never fires.
        socket.io.on('reconnect_attempt', () => {
            const tk = localStorage.getItem('accessToken');
            if (tk) {
                // The token rides in `auth` only — never in the URL.
                socket.auth = { ...(socket.auth || {}), token: tk };
            }
        });

        socket.on("connect", () => {
            console.log("✅ Support socket connected:", socket.id);
            console.log(`📡 Connected as ${role} with ID: ${id}`);
            console.log(`🔌 Transport:`, socket.io.engine.transport.name); // Shows 'polling' or 'websocket'
            isConnecting = false;

            // Attach any pending listeners
            if (isAgentRole(role) && pendingCallbacks.admin.receiveMessage) {
                console.log('🎧 Attaching pending receiveSupportMessage listener for agent');
                socket.off('receiveSupportMessage');
                socket.on('receiveSupportMessage', pendingCallbacks.admin.receiveMessage);
                pendingCallbacks.admin.receiveMessage = null;
            } else if ((role === 'doctor' || role === 'patient') && pendingCallbacks.user.receiveMessage) {
                console.log('🎧 Attaching pending receiveSupportMessage listener for user');
                socket.off('receiveSupportMessage');
                socket.on('receiveSupportMessage', pendingCallbacks.user.receiveMessage);
                pendingCallbacks.user.receiveMessage = null;
            }

            // Agents join nothing at connect: the lists arrive only after
            // `requestAdminTickets { queue }`, which the page emits itself.
            //
            // On a RECONNECT, though, the page will not ask again — and the
            // server's fresh socket is in no room, so the live queue updates
            // would silently stop. Re-request the last queue to rejoin it.
            if (isAgentRole(role)) {
                if (hasConnectedOnce && lastQueue) {
                    console.log('🔄 Reconnected — rejoining queue:', lastQueue);
                    socket.emit('requestAdminTickets', { queue: lastQueue });
                }
                const hasUnassigned = socket.listeners('unassignedTickets').length > 0;
                const hasAdmin = socket.listeners('adminTickets').length > 0;
                const hasAllOpen = socket.listeners('allOpenTickets').length > 0;
                const hasClosed = socket.listeners('closedTickets').length > 0;
                if (!hasUnassigned || !hasAdmin || !hasAllOpen || !hasClosed) {
                    console.warn('⚠️ Some ticket list listeners are not attached yet — the page attaches them before requesting.');
                }
            }
            hasConnectedOnce = true;
        });

        // Log when transport upgrades to WebSocket
        socket.io.engine.on("upgrade", () => {
            console.log("⬆️ Upgraded to:", socket.io.engine.transport.name);
        });

        socket.on("disconnect", (reason) => {
            console.log("❌ Support socket DISCONNECTED:", reason);
            console.log("❌ Disconnect reason details:", {
                reason,
                timestamp: new Date().toISOString(),
                socketId: socket?.id,
                role: connectionRole,
                userId: connectionId
            });
            isConnecting = false;

            // Show user-friendly message for unexpected disconnections
            if (reason === 'io server disconnect') {
                toast.warning('Support service disconnected. Reconnecting...');
            } else if (reason === 'transport close' || reason === 'transport error') {
                toast.warning('Connection lost. Attempting to reconnect...');
            }
        });

        socket.on("connect_error", (err) => {
            console.error("❌ Support socket connection error:", err);
            console.error("   Error details:", {
                message: err.message,
                type: err.type,
                description: err.description
            });
            isConnecting = false;
            toast.warning('Failed to connect to support service. Please check your connection.');
        });

        socket.on("error", (error) => {
            console.error("❌ Support socket error:", error);

            // A CONNECTED SOCKET CAN STILL BE UNAUTHENTICATED.
            //
            // socket.user is set from the token presented at HANDSHAKE. Access
            // tokens last ~15 minutes and a socket lives as long as the tab, so
            // any page left open long enough ends up connected but anonymous.
            // The server answers the structured codes on `supportError` (the
            // page's own listener recovers there, see onSupportError); this
            // legacy channel carries the same codes, so recover here as well.
            if (handleAuthError(error, (e) => toast.warning(e?.message || 'Support service error occurred'))) return;
            toast.warning(error?.message || 'Support service error occurred');
        });

        socket.io.on("reconnect", (attemptNumber) => {
            console.log("🔄 Support socket RECONNECTED after", attemptNumber, "attempts");
            console.log("✅ Reconnection successful:", {
                socketId: socket?.id,
                role: connectionRole,
                userId: connectionId,
                timestamp: new Date().toISOString()
            });
            toast.success('Reconnected to support service');

            // Re-emit initialization events after reconnection
            // Backend needs to know who we are again after reconnect
            if (isAgentRole(connectionRole)) {
                if (lastQueue) {
                    console.log('🔄 Re-requesting queue lists after reconnect:', lastQueue);
                    socket.emit('requestAdminTickets', { queue: lastQueue });
                }
            } else if (connectionRole === 'doctor' || connectionRole === 'patient') {
                console.log('🔄 Re-requesting user tickets after reconnect');
                socket.emit('requestUserTickets', {});
            }
        });

        socket.io.on("reconnect_attempt", (attemptNumber) => {
            console.log(`🔄 Reconnection attempt #${attemptNumber}...`);
        });

        socket.io.on("reconnect_error", (err) => {
            console.error("❌ Reconnection error:", err);
        });

        socket.io.on("reconnect_failed", () => {
            console.error("❌ Reconnection failed after all attempts");
            toast.warning('Unable to reconnect to support service. Please refresh the page.');
        });

        return socket;
    } catch (error) {
        console.error('Error creating support socket:', error);
        isConnecting = false;
        throw error;
    }
};

/**
 * Get existing socket instance
 * @returns {Object|null} Socket instance or null
 */
export const getSupportSocket = () => {
    if (!socket && !socketWarningShown) {
        // Only warn once to avoid console spam
        socketWarningShown = true;
        console.log('ℹ️ Support socket not initialized yet. It will be created automatically.');
    }
    return socket;
};

/**
 * Disconnect support socket
 */
export const disconnectSupportSocket = () => {
    if (socket) {
        console.log('🔌 Disconnecting support socket:', socket.id);
        console.log('   Removing all listeners and closing connection');
        socket.removeAllListeners();
        socket.io?.removeAllListeners?.();
        socket.disconnect();
        socket = null;
        isConnecting = false;
        connectionRole = null;
        connectionId = null;
        lastQueue = null;
        hasConnectedOnce = false;
        console.log('✅ Socket disconnected and cleaned up');
    } else {
        console.log('⚠️ No socket to disconnect');
    }
};

/**
 * Check if socket is connected
 * @returns {boolean}
 */
export const isSupportSocketConnected = () => {
    return socket?.connected || false;
};

/**
 * Manually reconnect the socket
 * @returns {boolean} - true if reconnection initiated, false if no socket exists
 */
export const reconnectSupportSocket = () => {
    if (socket && !socket.connected) {
        console.log('🔄 Manually reconnecting support socket...');
        console.log('   Previous role:', connectionRole, 'ID:', connectionId);
        socket.connect();
        return true;
    } else if (!socket) {
        console.warn('⚠️ No socket instance to reconnect. Use connectSupportSocket() instead.');
        return false;
    } else {
        console.log('✅ Socket already connected');
        return false;
    }
};

// Attach a list listener now, or shortly after if the socket is still being
// created. The lists only ever arrive in answer to requestAdminTickets, so a
// listener attached a moment late still catches them.
const attachListListener = (event, callback) => {
    const attach = () => {
        socket.off(event);
        socket.on(event, (data) => {
            console.log(`📥 LISTEN: ${event} received:`, Array.isArray(data) ? `${data.length} rows` : data);
            callback(data);
        });
    };
    if (socket) {
        attach();
    } else {
        console.warn(`⚠️ Cannot attach ${event} listener: socket not available yet`);
        setTimeout(() => { if (socket) attach(); }, 500);
    }
};

// Plain "replace the listener" helper for one-shot server events.
const listen = (event, callback) => {
    if (socket) { socket.off(event); socket.on(event, callback); }
};

// Agent-side (Super Admin + platform staff) socket events.
//
// PAYLOADS CARRY NO ACTOR. Who accepts, assigns, replies or closes is the
// verified socket.user on the server — an adminId in the payload would be a
// value anyone could type. Every emit here therefore sends only the ticket
// and the target, never "who I am".
export const adminSocketEvents = {
    // Ask for the four lists of ONE queue. The server joins the socket to
    // `sa_support_${queue}` and answers with unassignedTickets, adminTickets
    // (= mine, computed server-side), allOpenTickets and closedTickets.
    requestTickets: ({ queue } = {}) => {
        const q = queue || lastQueue;
        if (!q) {
            console.error('❌ Cannot request tickets: no queue given');
            return;
        }
        lastQueue = q;
        if (socket) {
            if (socket.connected) {
                console.log('📤 EMIT: requestAdminTickets', { queue: q });
                socket.emit('requestAdminTickets', { queue: q });
            } else {
                console.warn('⚠️ Cannot request tickets: socket not connected');
                // Wait for connection and then request
                socket.once('connect', () => {
                    console.log('📤 EMIT: requestAdminTickets (after connection)', { queue: q });
                    socket.emit('requestAdminTickets', { queue: q });
                });
            }
        } else {
            console.error('❌ Cannot request tickets: socket not available');
        }
    },

    // Listen for ticket lists
    onUnassignedTickets: (callback) => attachListListener('unassignedTickets', callback),
    onAdminTickets: (callback) => attachListListener('adminTickets', callback),
    onAllOpenTickets: (callback) => attachListListener('allOpenTickets', callback),
    onClosedTickets: (callback) => attachListListener('closedTickets', callback),
    onAdminTicketListUpdate: (callback) => listen('adminTicketListUpdate', callback),
    onNewSupportTicket: (callback) => {
        if (socket) {
            socket.off('newSupportTicket');
            socket.on('newSupportTicket', (data) => {
                console.log('📥 LISTEN: newSupportTicket', data);
                callback(data);
            });
        }
    },

    // Accept ticket (assign to myself)
    acceptTicket: (ticketId) => {
        if (socket) {
            console.log('📤 EMIT: acceptSupportTicket', { ticketId });
            socket.emit('acceptSupportTicket', { ticketId });
        }
    },
    onTicketAccepted: (callback) => listen('ticketAccepted', callback),
    onTicketAlreadyAssigned: (callback) => listen('ticketAlreadyAssigned', callback),

    // Join ticket room (participant check server-side)
    joinTicket: (ticketId) => {
        if (socket) socket.emit('joinSupportTicket', { ticketId });
    },
    onTicketJoined: (callback) => listen('supportTicketJoined', callback),

    // Send message — { ticketId, message, replyTo? }
    sendMessage: (data) => {
        if (socket) {
            console.log('📤 AGENT: Emitting sendSupportMessage:', data);
            socket.emit('sendSupportMessage', data);
        } else {
            console.error('❌ AGENT: Socket not available when trying to send message');
        }
    },
    onReceiveMessage: (callback) => {
        const wrappedCallback = (data) => {
            console.log('📥 AGENT: Received receiveSupportMessage event:', data);
            callback(data);
        };

        if (socket && socket.connected) {
            socket.off('receiveSupportMessage');
            console.log('🎧 AGENT: Attaching receiveSupportMessage listener (socket connected)');
            socket.on('receiveSupportMessage', wrappedCallback);
        } else if (socket && socket.connecting) {
            console.log('⏳ AGENT: Socket connecting, storing callback to attach when ready');
            pendingCallbacks.admin.receiveMessage = wrappedCallback;
            // Also attach when socket connects
            socket.once('connect', () => {
                socket.off('receiveSupportMessage');
                console.log('🎧 AGENT: Socket connected, attaching receiveSupportMessage listener');
                socket.on('receiveSupportMessage', wrappedCallback);
            });
        } else {
            console.log('⏳ AGENT: Socket not available, storing callback');
            pendingCallbacks.admin.receiveMessage = wrappedCallback;
        }
    },

    // Close ticket
    closeTicket: (ticketId) => {
        if (socket) socket.emit('closeSupportTicket', { ticketId });
    },
    onTicketClosed: (callback) => listen('supportTicketClosed', callback),

    // Fetch message history
    fetchHistory: (ticketId, page = 1, limit = 100) => {
        if (socket) socket.emit('fetchSupportHistory', { ticketId, page, limit });
    },
    onHistory: (callback) => listen('supportHistory', callback),

    // Assign an unassigned ticket to another agent
    assignTicket: (ticketId, adminId) => {
        if (socket) {
            console.log('📤 EMIT: assignSupportTicket', { ticketId, adminId });
            socket.emit('assignSupportTicket', { ticketId, adminId });
        }
    },
    onTicketAssignedSuccess: (callback) => listen('ticketAssignedSuccess', callback),
    // Broadcast to the ticket room when the assignee changes.
    onTicketAssigned: (callback) => listen('ticketAssigned', callback),

    // Reassign a ticket that already has an agent
    reassignTicket: (ticketId, newAdminId) => {
        if (socket) {
            console.log('📤 EMIT: reassignSupportTicket', { ticketId, newAdminId });
            socket.emit('reassignSupportTicket', { ticketId, newAdminId });
        }
    },
    onTicketReassignmentSuccess: (callback) => listen('ticketReassignmentSuccess', callback),
    onTicketReassigned: (callback) => listen('ticketReassigned', callback),

    // Who may take a ticket in this queue (socket twin of GET
    // /platform-staff/agents?queue=). The page uses the REST route through
    // redux; this stays for callers that already hold the socket.
    fetchSupportAgents: ({ queue } = {}) => {
        if (socket) socket.emit('fetchSupportAgents', { queue: queue || lastQueue });
    },
    onSupportAgents: (callback) => listen('supportAgents', callback),

    // { code, message } from any refused agent event. An auth-shaped code
    // first tries to re-authenticate in place (then re-runs the page's last
    // request through onReauthenticated); the page sees the error only when
    // that does not help.
    onSupportError: (callback) => listen('supportError', (err) => {
        if (handleAuthError(err, callback)) return;
        callback(err);
    }),

    // Listen for reconnect events (used by components to re-fetch data)
    // socket.io-client v4 emits reconnect lifecycle events on the Manager
    // (socket.io), not the Socket — a socket-level listener never fires.
    onReconnect: (callback) => {
        if (!socket?.io) return () => {};
        socket.io.on('reconnect', callback);
        return () => socket.io.off('reconnect', callback);
    },

    // Remove listeners
    removeListener: (event) => {
        if (socket) socket.off(event);
    }
};

// User (Doctor/Patient) socket events — the raiser side. Unused by this
// portal today; kept so the service stays a drop-in twin of the tenant app's.
export const userSocketEvents = {
    requestTickets: () => {
        if (socket) {
            if (socket.connected) {
                socket.emit('requestUserTickets', {});
            } else {
                socket.once('connect', () => socket.emit('requestUserTickets', {}));
            }
        }
    },
    onUserTickets: (callback) => listen('userTickets', callback),
    onUserTicketListUpdate: (callback) => listen('userTicketListUpdate', callback),
    initTicket: ({ categoryId, subCategoryId }) => {
        if (socket) socket.emit('initSupportTicket', { categoryId, subCategoryId });
    },
    onTicketInitialized: (callback) => listen('supportTicketInitialized', callback),
    joinTicket: (ticketId) => {
        if (socket) socket.emit('joinSupportTicket', { ticketId });
    },
    onTicketJoined: (callback) => listen('supportTicketJoined', callback),
    sendMessage: (data) => {
        if (socket) socket.emit('sendSupportMessage', data);
    },
    onReceiveMessage: (callback) => {
        if (socket && socket.connected) {
            socket.off('receiveSupportMessage');
            socket.on('receiveSupportMessage', callback);
        } else {
            pendingCallbacks.user.receiveMessage = callback;
        }
    },
    onTicketAssigned: (callback) => listen('ticketAssigned', callback),
    onTicketStatusChanged: (callback) => listen('ticketStatusChanged', callback),
    closeTicket: (ticketId) => {
        if (socket) socket.emit('closeSupportTicket', { ticketId });
    },
    onTicketClosed: (callback) => listen('supportTicketClosed', callback),
    onTicketAlreadyOpen: (callback) => listen('supportTicketAlreadyOpen', callback),
    onSupportError: (callback) => listen('supportError', (err) => {
        if (handleAuthError(err, callback)) return;
        callback(err);
    }),
    fetchHistory: (ticketId, page = 1, limit = 100) => {
        if (socket) socket.emit('fetchSupportHistory', { ticketId, page, limit });
    },
    onHistory: (callback) => listen('supportHistory', callback),
    // socket.io-client v4 emits reconnect lifecycle events on the Manager
    // (socket.io), not the Socket — a socket-level listener never fires.
    onReconnect: (callback) => {
        if (!socket?.io) return () => {};
        socket.io.on('reconnect', callback);
        return () => socket.io.off('reconnect', callback);
    },
    removeListener: (event) => {
        if (socket) socket.off(event);
    }
};

export default {
    connectSupportSocket,
    getSupportSocket,
    disconnectSupportSocket,
    isSupportSocketConnected,
    reconnectSupportSocket,
    setOnReauthenticated,
    adminSocketEvents,
    userSocketEvents
};
